import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.35,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

function SubmissionConfirmPage() {
  const navigate = useNavigate();
  const topRef = useRef(null);

  const [userInfo, setUserInfo] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useLayoutEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, []);

  useEffect(() => {
    // 신분증 이미지 로드
    let image = null;
    try {
      image = localStorage.getItem("temp_captured_image");
    } catch (err) {}

    if (!image) {
      // 촬영된 신분증이 없으면 제출 페이지로 되돌림
      navigate("/id-card-submit", { replace: true });
      return;
    }
    setCapturedImage(image);

    // 입력 정보 로드 (USER_INFO 쿠키)
    try {
      const userInfoFromCookie = Cookies.get("USER_INFO");
      if (userInfoFromCookie) {
        setUserInfo(JSON.parse(userInfoFromCookie));
      }
    } catch (err) {}
  }, [navigate]);

  // USER_INFO → 화면에 표시할 값으로 포맷 (CompletionPage와 동일한 매핑)
  const formatUserData = () => {
    const defaultData = {
      dong: "-",
      ho: "-",
      name: "-",
      shareName: "-",
      apartment: "-",
    };

    if (!userInfo) return defaultData;

    return {
      dong: userInfo.claimApartmentBlock || defaultData.dong,
      ho: userInfo.claimApartmentUnit || defaultData.ho,
      name: userInfo.claimAssignmentName || defaultData.name,
      shareName: userInfo.shareName || defaultData.shareName,
      apartment: userInfo.apartmentName || defaultData.apartment,
    };
  };

  const userData = formatUserData();

  // 신분증 사진 수정 (제출 페이지에서 다시 촬영/선택)
  const handleEditPhoto = () => {
    navigate("/id-card-submit");
  };

  const getPresignedUrls = async (files) => {
    const response = await axios.get(
      `${API_ENDPOINTS.BASE_URL}/mobile/file/upload`,
      {
        params: {
          type: "image",
          mimeType: "image/png",
          imageUploadTarget: "claimIdCardImage",
          num: files.length,
        },
        headers: {
          Authorization: `Bearer ${Cookies.get(COOKIE_KEYS.ACCESS_TOKEN)}`,
        },
      }
    );
    return response.data;
  };

  // 확인 → 신분증 제출까지 진행 후 완료 페이지로 이동
  const handleConfirm = async () => {
    if (!capturedImage) {
      navigate("/id-card-submit");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // 1. Base64 이미지를 File 객체로 변환
      const imageFile = await (async () => {
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        return new File([blob], `idcard_${Date.now()}.png`, {
          type: "image/png",
        });
      })();

      // 2. Presigned URL 가져오기
      setUploadProgress(20);
      const presignedUrlData = await getPresignedUrls([imageFile]);

      if (!presignedUrlData || !presignedUrlData[0]?.url) {
        throw new Error("Presigned URL을 가져오는데 실패했습니다.");
      }

      setUploadProgress(40);

      // 3. S3에 업로드
      const presignedUrl = presignedUrlData[0].url;
      await axios.put(presignedUrl, imageFile, {
        headers: {
          "Content-Type": "image/png",
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 40) / progressEvent.total
          );
          setUploadProgress(40 + progress); // 40% ~ 80%
        },
      });

      // S3 URL 추출 (query string 제거)
      const s3Url = presignedUrl.split("?")[0];

      // S3 URL에서 상대 경로만 추출
      const relativePath = API_ENDPOINTS.extractRelativePath(s3Url);

      setUploadProgress(90);

      // 4. 신분증 이미지 URL을 서버에 전송
      const verificationCode = Cookies.get("VERIFICATION_CODE");

      const uploadResponse = await fetch(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/share/claimIdCardImage/${verificationCode}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            claimIdCardImage: relativePath, // 상대 경로만 전송
          }),
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("신분증 제출에 실패했습니다.");
      }

      setUploadProgress(95);

      // 5. 상태 확인 API 호출
      const statusResponse = await fetch(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/share/check/status/${verificationCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!statusResponse.ok) {
        throw new Error("상태 확인에 실패했습니다.");
      }

      // 모든 과정이 완료되면 completion 페이지로 이동
      setUploadProgress(100);
      setTimeout(() => {
        navigate("/completion");
      }, 500);
    } catch (error) {
      setUploadError("신분증을 재촬영후 다시 제출해주세요.");
      setShowErrorModal(true);
      setTimeout(() => {
        setShowErrorModal(false);
      }, 3000);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <motion.div
      ref={topRef}
      style={{
        padding: "25px",
        height: "100vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
      }}
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <motion.div
        className="page-header"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        style={{ marginTop: "0", marginBottom: "20px" }}
      >
        <h1>제출 내용 확인</h1>
      </motion.div>

      {/* 위임 정보 요약 */}
      <div className="contract-summary" style={{ marginBottom: "20px" }}>
        <h3 style={{ textAlign: "left" }}>위임 정보</h3>
        <div className="summary-row">
          <div className="summary-label">동·호수</div>
          <div className="summary-value">
            {userData.dong}동 {userData.ho}호
          </div>
        </div>
        <div className="summary-row">
          <div className="summary-label">소유자 성명</div>
          <div className="summary-value">{userData.name}</div>
        </div>
        <div className="summary-row">
          <div className="summary-label">위임자</div>
          <div className="summary-value">{userData.shareName}</div>
        </div>
        <div className="summary-row">
          <div className="summary-label">수임자</div>
          <div className="summary-value">
            {userData.apartment} 입주자대표회의
          </div>
        </div>
        <div className="summary-row">
          <div className="summary-label">위임 권한</div>
          <div className="summary-value">의결권 행사</div>
        </div>
      </div>

      {/* 신분증 사진 */}
      <div className="contract-summary" style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px",
          }}
        >
          <h3 style={{ margin: 0, textAlign: "left" }}>신분증 사진</h3>
          <button
            onClick={handleEditPhoto}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "none",
              border: "none",
              color: "#7A4FFF",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px",
              WebkitTapHighlightColor: "rgba(0,0,0,0)",
            }}
          >
            <img
              src="/reset.png"
              alt="수정"
              style={{ width: "16px", height: "16px", pointerEvents: "none" }}
            />
            사진 수정
          </button>
        </div>
        {capturedImage && (
          <img
            src={capturedImage}
            alt="촬영된 신분증"
            style={{
              width: "100%",
              height: "auto",
              aspectRatio: "325/208",
              objectFit: "contain",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
            }}
          />
        )}
      </div>

      {/* 하단 버튼 */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginTop: "auto",
          marginBottom: "20px",
        }}
      >
        <button
          onClick={handleEditPhoto}
          style={{
            backgroundColor: "#EBEBFF",
            color: "#7A4FFF",
            height: "56px",
            width: "100%",
            maxWidth: "460px",
            borderRadius: "8px",
            border: "none",
            fontSize: "16px",
            fontWeight: 700,
            margin: "0 auto",
            cursor: "pointer",
            WebkitTapHighlightColor: "rgba(0,0,0,0)",
            WebkitAppearance: "none",
          }}
        >
          신분증 사진 수정
        </button>
        <button
          onClick={handleConfirm}
          disabled={isUploading}
          style={{
            backgroundColor: "#7A4FFF",
            color: "#FFFFFF",
            height: "56px",
            width: "100%",
            maxWidth: "460px",
            borderRadius: "8px",
            border: "none",
            fontSize: "16px",
            fontWeight: 700,
            margin: "0 auto",
            cursor: isUploading ? "default" : "pointer",
            opacity: isUploading ? 0.7 : 1,
            WebkitTapHighlightColor: "rgba(0,0,0,0)",
            WebkitAppearance: "none",
          }}
        >
          확인하고 제출하기
        </button>
      </div>

      {/* 업로드 진행 오버레이 */}
      {isUploading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "12px",
              width: "80%",
              maxWidth: "300px",
            }}
          >
            <div style={{ marginBottom: "15px", textAlign: "center" }}>
              제출 중... {uploadProgress}%
            </div>
            <div
              style={{
                width: "100%",
                height: "4px",
                backgroundColor: "#e0e0e0",
                borderRadius: "2px",
              }}
            >
              <div
                style={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  backgroundColor: "#7A4FFF",
                  borderRadius: "2px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showErrorModal && (
        <motion.div
          className="error-modal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <p>{uploadError}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default SubmissionConfirmPage;
